import { useState, useCallback, useEffect } from 'react';
import { api } from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { getBrowserTimeZone } from '@/lib/timezones';

const browserTz = getBrowserTimeZone();

export interface UseProfileReturn {
  displayName: string;
  setDisplayName: (v: string) => void;
  timeZone: string;
  setTimeZone: (v: string) => void;
  saving: boolean;
  handleSave: () => Promise<void>;
}

export function useProfile(): UseProfileReturn {
  const { user, refreshSession } = useAuth();
  const [displayName, setDisplayName] = useState(user?.displayName || '');
  const [timeZone, setTimeZone] = useState(user?.timeZone ?? browserTz);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDisplayName(user?.displayName || '');
    setTimeZone(user?.timeZone ?? browserTz);
  }, [user?.id, user?.displayName, user?.timeZone]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      await api.auth.updateProfile({ displayName, timeZone });
      await refreshSession();
      toast.success('Profile updated');
    } catch {
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  }, [displayName, timeZone, refreshSession]);

  return { displayName, setDisplayName, timeZone, setTimeZone, saving, handleSave };
}
